import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { v4 } from 'uuid'

import trashIcon from '@assets/images/trash-icon.svg'
import AddIcon from '@mui/icons-material/AddRounded'

import { Input } from '@components/Input'

import { PopUpImportResponses } from '../../../PopUpImportResponses'

import { FieldType, GridTextFieldModel, TextFieldModel } from '@types'

import {
  AddOptionButton,
  ButtonContainer,
  Container,
  GridInputContainer,
  GridLayout,
  ImportResponsesButton,
  Label,
  RowTitle,
  SectionContainer,
} from './FormFieldGridText.styled'

type FormFieldGridTextContentProps = {
  field: GridTextFieldModel
  projectId: string
  onChange: (value: Partial<GridTextFieldModel>) => void
}

export const FormFieldGridTextContent: React.FC<FormFieldGridTextContentProps> = ({
  field,
  projectId,
  onChange,
}: FormFieldGridTextContentProps) => {
  const { t } = useTranslation()
  const [importing, setImporting] = useState<boolean>(false)

  const createNewColumn = (): TextFieldModel => ({
    id: v4(),
    type: FieldType.Text,
    label: '',
    required: false,
    value: undefined,
  })

  const handleAddRow = () => {
    const newRowId = v4()
    const existingColumns = field.rows.length > 0 ? field.rows[0].columns : []

    const newColumns = existingColumns.map(() => createNewColumn())

    if (newColumns.length === 0) {
      newColumns.push(createNewColumn())
    }

    const newRow = {
      id: newRowId,
      label: '',
      columns: newColumns,
    }

    onChange({
      rows: [...field.rows, newRow],
    })
  }

  const handleAddColumn = () => {
    if (field.rows.length === 0) {
      const newRow = {
        id: v4(),
        label: '',
        columns: [createNewColumn()],
      }

      onChange({
        rows: [newRow],
      })
    }

    const updatedRows = field.rows.map((row) => {
      return {
        ...row,
        columns: [...row.columns, createNewColumn()],
      }
    })

    onChange({
      rows: updatedRows,
    })
  }

  const handleRowLabelChange = (rowIndex: number, value: string) => {
    const updatedRows = [...field.rows]
    updatedRows[rowIndex] = {
      ...updatedRows[rowIndex],
      label: value,
    }

    onChange({ rows: updatedRows })
  }

  const handleColumnLabelChange = (columnIndex: number, value: string) => {
    const updatedRows = field.rows.map((row) => {
      const updatedColumns = [...row.columns]
      if (columnIndex < updatedColumns.length) {
        updatedColumns[columnIndex] = {
          ...updatedColumns[columnIndex],
          label: value,
        }
      }
      return { ...row, columns: updatedColumns }
    })

    onChange({ rows: updatedRows })
  }

  const handleDeleteRow = (rowIndex: number) => {
    if (field.rows.length <= 1) return

    const updatedRows = [...field.rows]
    updatedRows.splice(rowIndex, 1)

    onChange({
      rows: updatedRows,
    })
  }

  const handleDeleteColumn = (columnIndex: number) => {
    if (field.rows.length === 0 || field.rows[0].columns.length <= 1) return

    const updatedRows = field.rows.map((row) => {
      const updatedColumns = [...row.columns]
      updatedColumns.splice(columnIndex, 1)
      return { ...row, columns: updatedColumns }
    })

    onChange({
      rows: updatedRows,
    })
  }

  return (
    <>
      <Container>
        <GridLayout>
          <GridInputContainer>
            <RowTitle>
              <Label>{t('modules_project-form-models_components_FormFieldGridText_rows')}</Label>
              <ImportResponsesButton type="button" onClick={() => setImporting(true)}>
                <span>{t('modules_project-form-models_components_FormFieldGridText_import_responses')}</span>
              </ImportResponsesButton>
            </RowTitle>

            {field.rows.map((row, rowIndex) => (
              <SectionContainer key={row.id}>
                <Input
                  type="text"
                  required
                  value={row.label || ''}
                  onChange={(value) => handleRowLabelChange(rowIndex, value)}
                />
                {field.rows.length > 1 && (
                  <ButtonContainer
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      handleDeleteRow(rowIndex)
                    }}
                  >
                    <img src={trashIcon} alt="Delete" />
                  </ButtonContainer>
                )}
              </SectionContainer>
            ))}

            <AddOptionButton type="button" onClick={handleAddRow}>
              <AddIcon />
              <span>{t('modules_project-form-models_components_FormFieldGridText_add_row')}</span>
            </AddOptionButton>
          </GridInputContainer>

          <GridInputContainer>
            <Label>{t('modules_project-form-models_components_FormFieldGridText_columns')}</Label>
            {field.rows.length > 0 &&
              field.rows[0].columns.map((column, columnIndex) => (
                <SectionContainer key={column.id}>
                  <Input
                    required
                    type="text"
                    value={column.label || ''}
                    onChange={(value) => handleColumnLabelChange(columnIndex, value)}
                  />
                  {field.rows[0].columns.length > 1 && (
                    <ButtonContainer
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        handleDeleteColumn(columnIndex)
                      }}
                    >
                      <img src={trashIcon} alt="Delete" />
                    </ButtonContainer>
                  )}
                </SectionContainer>
              ))}

            <AddOptionButton type="button" onClick={handleAddColumn}>
              <AddIcon />
              <span>{t('modules_project-form-models_components_FormFieldGridText_add_column')}</span>
            </AddOptionButton>
          </GridInputContainer>
        </GridLayout>
      </Container>

      {importing && (
        <PopUpImportResponses
          projectId={projectId}
          onSubmit={(response) => {
            const newOptions = response.map((value) => ({ id: v4(), label: value }))
            const columns = field.rows[0]?.columns || []
            const newRows = newOptions.map((option) => ({
              id: v4(),
              label: option.label,
              columns: columns.map((col) => ({
                ...col,
                id: v4(),
              })),
            }))

            onChange({ rows: newRows })

            setImporting(false)
          }}
          onClose={() => {
            setImporting(false)
          }}
        />
      )}
    </>
  )
}
